import { getHexTileMapKey } from '../hexmap/model/keys';
import type { HexTileMap, HexTileMapKey } from '../hexmap/model/types';
import type { RuleSet } from '../rules/schema';
import { holdsStock } from './stock';
import { tileResources } from './systems/economy';
import { territoryOwners } from './territory';
import type { Building, EntityId, PlayerId, SimState, Unit, UnitType } from './types';

/**
 * Spies: units of a type with rules `stealth` are unseen by other players
 * until exposed. The sim treats such a unit as hidden from everyone but its
 * owner (`isHidden`): it can't be targeted (attack orders, combat, follow-ups),
 * is no threat to anyone's automation or barracks, and is a ghost to units —
 * it neither blocks nor is blocked by them and holds no tile, for its own side
 * too (so nothing about where others may stand or walk gives it away); rival
 * buildings still bar its way. A type with `disguiseAs` can blend into a rival
 * completed city's territory (`blend`): while disguised its owner sees that
 * city's whole territory (vision.ts) and the city's owner sees one of their
 * own `disguiseAs` units there (`spyView`). It stays put; a move order,
 * leaving the territory, the city falling or being exposed ends the disguise
 * (systems/spies.ts). While disguised it holds its tile towards the city's
 * owner (`disguisedFor`, occupancy.ts), as a real unit of theirs would. The
 * city's owner may `expose` it: then it's seen, targeted and solid like any
 * unit, for good. An undisguised spy is caught (exposed) after
 * `pacing.spyGrace` seconds in a rival completed city's territory
 * (`Unit.lurk`), so staying there means blending in.
 */

/** Whether `unit` is hidden from every player but its owner (stealthy and not exposed). */
function isHidden(rules: RuleSet, unit: Unit): boolean {
  return !!rules.units[unit.type]?.stealth && !unit.exposed;
}

/**
 * The rival completed city whose territory (`owners`) holds `unit`'s tile, if
 * any (a unit inside a building is in none).
 */
function rivalLandCity(
  state: SimState,
  rules: RuleSet,
  unit: Unit,
  owners: ReadonlyMap<HexTileMapKey, EntityId>
): Building | undefined {
  if (unit.inside) return undefined;
  const cityId = owners.get(getHexTileMapKey(unit.pos));
  const city = cityId !== undefined ? state.buildings[cityId] : undefined;
  return city && city.owner !== unit.owner && holdsStock(rules, city) ? city : undefined;
}

/**
 * The player a disguised, still `unit` passes for one of theirs with (its
 * disguise city's owner), towards whose units it holds its tile.
 */
function disguisedFor(state: SimState, unit: Unit): PlayerId | undefined {
  if (!unit.disguise || unit.inside || (unit.path && unit.path.length > 0)) return undefined;
  return state.buildings[unit.disguise.cityId]?.owner;
}

/**
 * The city `unit`'s disguise holds in, if it still holds: a stealthy,
 * unexposed unit standing still, outside any building, in the territory
 * (`owners`) of that completed city of another player.
 */
function disguiseCity(
  state: SimState,
  rules: RuleSet,
  unit: Unit,
  owners: ReadonlyMap<HexTileMapKey, EntityId>
): Building | undefined {
  if (!unit.disguise || !isHidden(rules, unit) || !rules.units[unit.type]?.disguiseAs) {
    return undefined;
  }
  if (unit.inside || (unit.path && unit.path.length > 0)) return undefined;
  const city = state.buildings[unit.disguise.cityId];
  if (!city || city.owner === unit.owner || !holdsStock(rules, city)) return undefined;
  return owners.get(getHexTileMapKey(unit.pos)) === city.id ? city : undefined;
}

type BlendRejection = 'unknown' | 'no-disguise' | 'exposed' | 'territory' | 'resource' | 'occupied';

type BlendCheck = { ok: true; cityId: EntityId } | { ok: false; reason: BlendRejection };

/**
 * Whether `playerId`'s unit `unitId` may blend in where it stands now.
 * Shared by the `blend` command and the UI. Checked in order:
 *   - unknown: no such unit, not theirs, their player is out, or it's inside
 *   - no-disguise: its type has no `disguiseAs` (nor `stealth`)
 *   - exposed: it has been exposed
 *   - territory: it isn't standing in a rival completed city's territory
 *   - resource: (with `terrain`) its tile has wild resources left: a
 *     "worker" standing there would look like a gatherer whose tile never
 *     runs out, a free tell for the city's owner, so it blends on open
 *     ground (grass, beach) or a used-up tile
 *   - occupied: another unit stands still on its tile (they'd share it; a
 *     ghost there, an undisguised spy, doesn't count)
 * `owners` (`territoryOwners`) may be passed in. Without `terrain` the
 * resource check is skipped (the `blend` command always passes it).
 */
function canBlend(
  state: SimState,
  rules: RuleSet,
  playerId: PlayerId,
  unitId: EntityId,
  owners?: ReadonlyMap<HexTileMapKey, EntityId>,
  terrain?: HexTileMap
): BlendCheck {
  const unit = state.units[unitId];
  const player = state.players[playerId];
  if (!unit || unit.owner !== playerId || !player || player.eliminated || unit.inside) {
    return { ok: false, reason: 'unknown' };
  }
  const def = rules.units[unit.type];
  if (!def?.stealth || !def.disguiseAs) return { ok: false, reason: 'no-disguise' };
  if (unit.exposed) return { ok: false, reason: 'exposed' };
  const city = rivalLandCity(state, rules, unit, owners ?? territoryOwners(state, rules));
  if (!city) return { ok: false, reason: 'territory' };
  if (terrain && (tileResources(state, { terrain, rules }, unit.pos)?.remaining ?? 0) > 0) {
    return { ok: false, reason: 'resource' };
  }
  const taken = Object.values(state.units).some(
    (u) =>
      u.id !== unitId &&
      !u.inside &&
      !(u.path && u.path.length > 0) &&
      u.pos.q === unit.pos.q &&
      u.pos.r === unit.pos.r &&
      (!isHidden(rules, u) || !!u.disguise)
  );
  if (taken) return { ok: false, reason: 'occupied' };
  return { ok: true, cityId: city.id };
}

/** Ticks `playerId` must still wait before their next `expose` (0: none). */
function exposeCooldownLeft(state: SimState, playerId: PlayerId): number {
  const until = state.players[playerId]?.exposeCooldownUntil;
  return until === undefined ? 0 : Math.max(0, until - state.tick);
}

/**
 * How `viewer` sees `unit`: 'own' (their unit), 'visible' (anyone's, not
 * stealthy or exposed), 'disguised' (a spy blended into one of the viewer's
 * cities: shown to them as one of their own units of type `as`) or 'hidden'
 * (not shown at all). Where it stands still has to be in the viewer's sight
 * to be seen, as for any unit.
 */
type SpyView =
  | { kind: 'own' }
  | { kind: 'visible' }
  | { kind: 'disguised'; as: UnitType }
  | { kind: 'hidden' };

function spyView(state: SimState, rules: RuleSet, unit: Unit, viewer: PlayerId): SpyView {
  if (unit.owner === viewer) return { kind: 'own' };
  if (!isHidden(rules, unit)) return { kind: 'visible' };
  const as = rules.units[unit.type]?.disguiseAs;
  const city = unit.disguise ? state.buildings[unit.disguise.cityId] : undefined;
  if (as && city?.owner === viewer) return { kind: 'disguised', as };
  return { kind: 'hidden' };
}

export type { BlendCheck, BlendRejection, SpyView };
export {
  canBlend,
  disguiseCity,
  disguisedFor,
  exposeCooldownLeft,
  isHidden,
  rivalLandCity,
  spyView,
};
